/**
 * The Peer pane's mutation subset (REQ-UI-154; storyboard Flow L §4c).
 *
 * # A subset, and the line is deliberate
 *
 * Per-interface enable/disable, delete at peer / interface / connection, and
 * Export. The rule is *acts an operator reaches for while looking at health* —
 * you notice an interface misbehaving and turn it off; you do not author a
 * filter here. Anything larger opens Flow W, so the status display does not
 * become a second surface where the same configuration is authored and has to
 * be kept in step with the first.
 *
 * **Start/stop is deliberately absent** even though it fits that description. It
 * already exists at every level in Flow W, and here it would sit one click from
 * a delete that looks like it — precisely the confusion Flow W's own copy exists
 * to prevent.
 *
 * # Absent, never disabled
 *
 * Nothing in this module renders for a non-privileged session (REQ-UI-143). On
 * the surface that shows what is leaving the deployment, a greyed-out Delete
 * tells an operator the capability exists and invites them to go looking for a
 * way to use it. The BFF enforces regardless; this hiding is cosmetic.
 *
 * # Every act names its blast radius
 *
 * An operator asked to approve something whose extent they have to guess is
 * being asked to approve something they have not been told. Deleting a peer says
 * how many interfaces go with it; deleting a connection says that queued data is
 * not redirected **and that this is not the same as stopping it**. Disable
 * confirms too — "the service stops loading this interface" is not what a word
 * as soft as *disable* leads an operator to expect, and enabling does not by
 * itself start anything.
 */
import type { PeerInterfaceView } from "@/peers/peer-model";
import { cn } from "@/lib/utils";

/** What a pending confirmation is about. */
export type PeerActionKind = "peer" | "interface" | "connection" | "disable" | "enable";

/** A small bordered action button; `danger` marks the irreversible ones. */
function ActionButton({
  onClick,
  testId,
  danger = false,
  children,
}: {
  onClick: () => void;
  testId: string;
  danger?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "rounded border px-2 py-0.5 text-meta focus:outline-none focus-visible:ring-1 focus-visible:ring-accent",
        danger
          ? "border-destructive text-destructive hover:bg-destructive/10"
          : "border-border text-text-primary hover:bg-bg-elevated",
      )}
    >
      {children}
    </button>
  );
}

/**
 * A confirmation strip that states what the act does and what it does not.
 *
 * Rendered where the operator clicked, and always with both choices visible —
 * a destructive confirmation whose safe option has to be found by scrolling is
 * biased the wrong way.
 */
export function ConfirmStrip({
  testId,
  message,
  confirmLabel,
  danger,
  onConfirm,
  onCancel,
}: {
  testId: string;
  message: React.ReactNode;
  confirmLabel: string;
  danger: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.ReactElement {
  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn(
        "mt-1 flex flex-wrap items-center gap-2 rounded border px-2 py-1 text-meta",
        danger ? "border-destructive text-destructive" : "border-border text-text-primary",
      )}
    >
      <span className="min-w-[12rem] flex-1">{message}</span>
      <button
        type="button"
        onClick={onConfirm}
        data-testid={`${testId}-go`}
        className={cn(
          "rounded px-2 py-0.5",
          danger
            ? "bg-destructive text-destructive-foreground hover:opacity-90"
            : "border border-border text-text-primary hover:bg-bg-elevated",
        )}
      >
        {confirmLabel}
      </button>
      <button
        type="button"
        onClick={onCancel}
        data-testid={`${testId}-cancel`}
        className="rounded border border-border px-2 py-0.5 text-text-primary hover:bg-bg-elevated"
      >
        Keep
      </button>
    </div>
  );
}

/** Props for {@link InterfaceActionButtons}. */
export interface InterfaceActionButtonsProps {
  view: PeerInterfaceView;
  /** Arm a confirmation. The strip itself renders BELOW the row — see below. */
  onAsk: (kind: PeerActionKind) => void;
}

/**
 * Enable/disable and delete buttons for one interface, sized to sit inline with
 * its name and state pill.
 *
 * ⚠ **The buttons and their confirmation are separate components on purpose.**
 * The row is a `flex items-center` line; a confirmation rendered inside it
 * becomes another flex item and is squeezed to a sliver beside the pill. The
 * strip belongs below the row, so the arming state is held by the row and this
 * component only reports the intent. `tsc` and jsdom are both blind to that —
 * the DOM is correct either way.
 *
 * Returns `null` for a field-limited view: enable/disable is a whole-document
 * PUT, and a document rebuilt from a field-limited projection would blank the
 * endpoints and cert references of a live egress path. `toInterfaceConfig`
 * refuses that outright, so offering the control would be an affordance that
 * throws on click. Defence in depth behind the caller's role gate.
 */
export function InterfaceActionButtons({
  view,
  onAsk,
}: InterfaceActionButtonsProps): React.ReactElement | null {
  if (view.metadata === undefined) return null;
  return (
    <div className="flex flex-none items-center gap-1">
      <ActionButton
        testId={`peer-iface-toggle-enabled-${view.name}`}
        onClick={() => onAsk(view.enabled ? "disable" : "enable")}
      >
        {view.enabled ? "Disable" : "Enable"}
      </ActionButton>
      <ActionButton
        testId={`peer-iface-delete-${view.name}`}
        danger
        onClick={() => onAsk("interface")}
      >
        Delete&hellip;
      </ActionButton>
    </div>
  );
}

/** The confirmation for whichever interface act is armed, rendered below the row. */
export function InterfaceActionConfirm({
  view,
  pending,
  onSetEnabled,
  onDelete,
  onCancel,
}: {
  view: PeerInterfaceView;
  pending: PeerActionKind | null;
  onSetEnabled: (enabled: boolean) => void;
  onDelete: () => void;
  onCancel: () => void;
}): React.ReactElement | null {
  const connectionCount = view.connections.length;
  if (pending === "disable" || pending === "enable") {
    const enabling = pending === "enable";
    return (
      <ConfirmStrip
        testId={`peer-iface-confirm-${pending}-${view.name}`}
        danger={false}
        confirmLabel={enabling ? "Enable" : "Disable"}
        message={
          enabling ? (
            <>
              Enable interface <strong>{view.name}</strong>? The service loads it again and
              reapplies its recorded start/stop state — enabling does not by itself start it.
            </>
          ) : (
            <>
              Disable interface <strong>{view.name}</strong>? The service stops loading it entirely.
              Its recorded start/stop state is preserved and reapplied when it is enabled again.
            </>
          )
        }
        onConfirm={() => {
          onCancel();
          onSetEnabled(enabling);
        }}
        onCancel={onCancel}
      />
    );
  }
  if (pending === "interface") {
    return (
      <ConfirmStrip
        testId={`peer-iface-confirm-delete-${view.name}`}
        danger
        confirmLabel="Delete"
        message={
          <>
            Delete interface <strong>{view.name}</strong> and its {connectionCount}{" "}
            {connectionCount === 1 ? "connection" : "connections"}? This cannot be undone.
          </>
        }
        onConfirm={() => {
          onCancel();
          onDelete();
        }}
        onCancel={onCancel}
      />
    );
  }
  return null;
}
